import { describeStaffFailure, STAFF_WORDS } from "../failure-words";

// How the UI tells a staff member what went wrong. The API envelope has no rule code by design, so the
// HTTP status is all there is: 409 means "the item changed" or "a rule refused this" (the page tells them
// apart by reading the item again and comparing `version`), 422 means the values were refused, 403 means
// no access, 503 means a service (for media: the storage) is not available.

export type CatalogFailureKind =
  | "conflict"
  | "invalid"
  | "forbidden"
  | "not_found"
  | "unauthenticated"
  | "unavailable"
  | "network"
  | "response"
  | "unknown";

export type CatalogFailure = Readonly<{
  kind: CatalogFailureKind;
  message: string;
  /** The same command may be retried with the same idempotency key (no answer, or a 5xx: it may have run). */
  retryable: boolean;
  requestId: string | null;
}>;

const FORM_KEPT = "Your changes are still in the form.";

const INVALID = "The server did not accept these values. Check the fields and try again.";

export function describeCatalogError(error: unknown): CatalogFailure {
  const { kind, message, retryable, requestId } = describeStaffFailure<CatalogFailureKind>(error, {
    conflict: "The server refused this change because of the item's current state.",
    invalid: INVALID,
    forbidden: "Your role cannot do this. The catalog is read-only for you.",
    on: {
      // The catalog does not tell a 400 from a 422.
      bad_request: { kind: "invalid", message: INVALID },
      unauthenticated: `Your session has ended. Sign in again. ${FORM_KEPT}`,
      not_found: {
        kind: "not_found",
        message: "This item was not found. It may not exist, or you may not have access.",
      },
      network: `${STAFF_WORDS.network} ${FORM_KEPT}`,
      // A throttle is not retried here: it reads like any other status the catalog does not know.
      rate_limited: { kind: "unknown", message: STAFF_WORDS.otherStatus, retryable: false },
    },
  });
  return { kind, message, retryable, requestId };
}
